import api from 'lib/trpcClient';
import { Box } from '@mui/material';
import InteractiveTable, { Column, Row } from 'components/InteractiveTable';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { sumBy } from 'lodash';

import {
  dateToLabel,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { usePagination } from 'further-ui/hooks';
import { createLpInvestmentEditRoute } from 'adminConstants/routes';

type InvestmentHistoryProps = {
  vehicleId: string;
  companyId: string;
};

type InvestmentRow = {
  id: string;
  holdingClassId: string;
  investmentId: string;
  date: Date;
  name: string;
  currentNumberOfUnits: number;
  totalNumberOfUnits: number;
  investmentAmountTcy: number;
  currency: CurrencyCode;
  currentValue: number;
  currentMOIC: number;
  currentRealisedValue: number;
  currentUnrealisedValue: number;
  latestRevaluationDate?: Date;
} & Row;

const pageId = 'investment-history';

type InvestmentColumn = {
  headerName: string;
  field: keyof InvestmentRow;
  renderCell?: (params: { row: InvestmentRow }) => ReactNode;
} & Column;

const InvestmentHistory: React.FC<InvestmentHistoryProps> = ({
  vehicleId,
  companyId,
}) => {
  const theme = useTheme();
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'dealCompletionDate',
  });

  const [investmentHistory, query] =
    api.asset.getInvestmentHistory.useSuspenseQuery({
      vehicleId,
      assetId: companyId,
      ...pagination.properties,
    });

  const investmentHistoryRows = investmentHistory.map((investment) => ({
    ...investment,
    id: `${investment.holdingClassId}_${investment.investmentId}`,
  }));

  // Calculate totals for summary row
  const totalNumberOfUnits = sumBy(
    investmentHistoryRows,
    (row) => row.totalNumberOfUnits,
  );
  const totalInitialInvestment = sumBy(
    investmentHistoryRows,
    (row) => row.investmentAmountTcy,
  );
  const totalCurrentNumberOfUnits = sumBy(
    investmentHistoryRows,
    (row) => row.currentNumberOfUnits,
  );
  const totalCurrentRealisedValue = sumBy(
    investmentHistoryRows,
    (row) => row.currentRealisedValue,
  );
  const totalCurrentUnrealisedValue = sumBy(
    investmentHistoryRows,
    (row) => row.currentUnrealisedValue,
  );

  // Calculate MOIC from summed values
  const totalCurrentValue =
    totalCurrentRealisedValue + totalCurrentUnrealisedValue;
  const totalMOIC =
    totalInitialInvestment > 0
      ? numberToDisplayString(totalCurrentValue / totalInitialInvestment, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

  const currency = investmentHistoryRows[0]?.currency ?? 'GBP';

  const columns: Array<InvestmentColumn> = [
    {
      headerName: 'Investment date',
      field: 'date',
      flex: 1,
      renderCell: ({ row }) => (
        <Link
          to={createLpInvestmentEditRoute(row.investmentId)}
          style={{ color: theme.palette.text.green }}
        >
          {dateToLabel(row.date)}
        </Link>
      ),
    },
    {
      headerName: 'Holding class',
      field: 'name',
      sortable: false,
      flex: 1,
      summary: 'Total',
    },
    {
      headerName: 'Total number of units',
      field: 'totalNumberOfUnits',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => numberToDisplayString(row.totalNumberOfUnits),
      summary: numberToDisplayString(totalNumberOfUnits),
    },
    {
      headerName: 'Initial investment',
      field: 'investmentAmountTcy',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) =>
        numberToCurrencyString(row.investmentAmountTcy, {
          currency: row.currency,
        }),
      summary: numberToCurrencyString(totalInitialInvestment, {
        currency,
      }),
    },
    {
      headerName: 'Current number of units',
      field: 'currentNumberOfUnits',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => numberToDisplayString(row.currentNumberOfUnits),
      summary: numberToDisplayString(totalCurrentNumberOfUnits),
    },
    {
      headerName: 'Current unrealised value',
      field: 'currentUnrealisedValue',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) =>
        numberToCurrencyString(row.currentUnrealisedValue, {
          currency: row.currency,
        }),
      summary: numberToCurrencyString(totalCurrentUnrealisedValue, {
        currency,
      }),
    },
    {
      headerName: 'Current realised value',
      field: 'currentRealisedValue',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) =>
        numberToCurrencyString(row.currentRealisedValue, {
          currency: row.currency,
        }),
      summary: numberToCurrencyString(totalCurrentRealisedValue, {
        currency,
      }),
    },
    {
      headerName: 'Current MOIC',
      field: 'currentMOIC',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => row.currentMOIC,
      summary: totalMOIC,
    },
    {
      headerName: 'Last revaluation',
      field: 'latestRevaluationDate',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => dateToLabel(row.latestRevaluationDate),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <InteractiveTable
        fullHeight
        columns={columns}
        disableTableConfiguration
        loading={query.isFetching}
        rows={investmentHistoryRows}
        id={`${pageId}-table`}
        pagination={pagination}
      />
    </Box>
  );
};

export default InvestmentHistory;
